import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RestaurantMealSpaceSelection, RestaurantSpaceReservationDetailView } from '@/models/restaurant-manager'

export type DraftRow = {
  localRowId: string
  spaceUsageDetailId: string | null
  currentSelection: RestaurantMealSpaceSelection | null
}

export const useRestaurantMealSpaceDraft = (sortedDetails: RestaurantSpaceReservationDetailView[]) => {
  const nextLocalRowNumberRef = useRef(1)
  const draftRowsRef = useRef<DraftRow[]>([])
  const [draftRows, setDraftRows] = useState<DraftRow[]>(() => buildDraftRows(sortedDetails))

  useEffect(() => {
    draftRowsRef.current = draftRows
  }, [draftRows])

  useEffect(() => {
    setDraftRows(previousRows => syncDraftRows(sortedDetails, previousRows))
  }, [sortedDetails])

  const hasUnsavedRow = useMemo(() => draftRows.some(row => row.spaceUsageDetailId == null), [draftRows])

  const replaceDraftRows = useCallback((nextRows: DraftRow[]) => {
    draftRowsRef.current = nextRows
    setDraftRows(nextRows)
  }, [])

  const buildInitialRows = useCallback(() => {
    const savedRows = buildDraftRows(sortedDetails)
    if (savedRows.length > 0) {
      return savedRows
    }

    const nextRow = createEmptyDraftRow(`new-${nextLocalRowNumberRef.current}`)
    nextLocalRowNumberRef.current += 1
    return [nextRow]
  }, [sortedDetails])

  const addEmptyRow = useCallback(() => {
    const nextRow = createEmptyDraftRow(`new-${nextLocalRowNumberRef.current}`)
    nextLocalRowNumberRef.current += 1
    replaceDraftRows([...draftRowsRef.current, nextRow])
  }, [replaceDraftRows])

  const removeRow = useCallback(
    (rowId: string) => {
      replaceDraftRows(draftRowsRef.current.filter(row => row.localRowId !== rowId))
    },
    [replaceDraftRows],
  )

  const applySelection = useCallback(
    (rowId: string, selection: RestaurantMealSpaceSelection) => {
      const nextRows = draftRowsRef.current.map(row => (row.localRowId === rowId ? { ...row, currentSelection: selection } : row))
      replaceDraftRows(nextRows)
      return nextRows
    },
    [replaceDraftRows],
  )

  const removeRowById = useCallback(
    (rowId: string) => {
      replaceDraftRows(draftRowsRef.current.filter(row => row.localRowId !== rowId))
    },
    [replaceDraftRows],
  )

  const resetToSaved = useCallback(() => {
    replaceDraftRows(buildDraftRows(sortedDetails))
  }, [replaceDraftRows, sortedDetails])

  const getRow = useCallback((rowId: string) => draftRowsRef.current.find(row => row.localRowId === rowId) ?? null, [])

  const getCurrentRows = useCallback(() => draftRowsRef.current, [])

  return {
    draftRows,
    hasUnsavedRow,
    replaceDraftRows,
    buildInitialRows,
    addEmptyRow,
    removeRow,
    removeRowById,
    applySelection,
    resetToSaved,
    getRow,
    getCurrentRows,
  }
}

export const isSameSelection = (left: RestaurantMealSpaceSelection, right: RestaurantMealSpaceSelection) =>
  left.spaceId === right.spaceId && left.useDateStart === right.useDateStart && left.useDateEnd === right.useDateEnd

const createEmptyDraftRow = (localRowId: string): DraftRow => ({
  localRowId,
  spaceUsageDetailId: null,
  currentSelection: null,
})

const buildDraftRows = (details: RestaurantSpaceReservationDetailView[]): DraftRow[] =>
  details.map(detail => ({
    localRowId: detail.spaceUsageDetailId,
    spaceUsageDetailId: detail.spaceUsageDetailId,
    currentSelection: {
      spaceId: detail.spaceId,
      spaceName: detail.spaceName,
      useDateStart: detail.useDateStart,
      useDateEnd: detail.useDateEnd,
    },
  }))

const syncDraftRows = (details: RestaurantSpaceReservationDetailView[], previousRows: DraftRow[]): DraftRow[] => {
  const nextSavedRows = buildDraftRows(details)
  const unsavedEmptyRow = previousRows.find(row => row.spaceUsageDetailId == null && row.currentSelection == null) ?? null

  if (!unsavedEmptyRow) {
    return nextSavedRows
  }

  return [...nextSavedRows, unsavedEmptyRow]
}
